import { AccountInfoResult, AccountModel } from '@block-explorer/entities';
import { usePostAccountsWithInfo } from './event-store-hooks';
import { indexBy, uniq, concat } from 'remeda';
import { isValidAccountHash, isValidPublicKey } from '../utils/account';
import { useCallback } from 'react';

interface GetAccountInfo {
  loading: boolean;
  getAccountInfo: <T>(publicKey: string) => T | undefined;
  getAccountInfoByHash: (accountHash: string) => AccountInfoResult | undefined;
  getPublicKeyAccountHash: (accountHash: string) => string | undefined;
}

export const useCreateAccountsInfoGetter = (
  publicKeyList: string[] | null,
  accountHashList: string[] | null = []
): GetAccountInfo => {
  const validAccountHashList = accountHashList?.filter(isValidAccountHash);
  const knownAccountHashList =
    publicKeyList == null
      ? null
      : publicKeyList.map((key) => {
          if (isValidPublicKey(key)) {
            return AccountModel({
              publicKeyHex: key,
            }).getAccountHash();
          }

          return key;
        });

  const accountHashes = uniq(
    concat(knownAccountHashList || [], validAccountHashList || [])
  );

  const { data: accounts, loading: loadingAccounts } = usePostAccountsWithInfo(
    accountHashes.length === 0
      ? null
      : {
          account_hashes: accountHashes,
          includes: 'account_info,centralized_account_info,cspr_name',
          order_by: 'balance',
          page_size: accountHashes.length,
        }
  );

  const accountsInfoByHash = indexBy(accounts || [], (i) => i.account_hash);

  const getAccountInfo = useCallback(
    (publicKey: string): any | undefined => {
      const accountInfoByHash =
        isValidPublicKey(publicKey) &&
        accountsInfoByHash[
          AccountModel({
            publicKeyHex: publicKey,
          }).getAccountHash()
        ];

      return accountInfoByHash ? accountInfoByHash : undefined;
    },
    [accountsInfoByHash]
  );

  const getAccountInfoByHash = useCallback(
    (accountHash: string): AccountInfoResult | undefined => {
      const accountInfoByHash =
        isValidAccountHash(accountHash) && accountsInfoByHash[accountHash];

      return accountInfoByHash ? accountInfoByHash : undefined;
    },
    [accountsInfoByHash]
  );

  const getPublicKeyAccountHash = useCallback(
    (accountHash: string): string | undefined => {
      const publicKeyByHash =
        isValidAccountHash(accountHash) &&
        accountsInfoByHash[accountHash]?.public_key;

      return publicKeyByHash ? publicKeyByHash : undefined;
    },
    [accountsInfoByHash]
  );

  return {
    getAccountInfo,
    getAccountInfoByHash,
    getPublicKeyAccountHash,
    loading: loadingAccounts,
  };
};
